import { defineStore } from 'pinia';
import { Amplify } from 'aws-amplify';
import type { ResourcesConfig } from 'aws-amplify';
import { useLogger } from '../composables/useLogger';
import { useCacheStore } from './cache';

type EnvironmentKey = 'dev' | 'test' | 'uat' | 'live';

const DB_NAME_ENV_MAP: Record<EnvironmentKey, string> = {
  dev: 'VITE_DB_NAME_DEV',
  test: 'VITE_DB_NAME_TEST',
  uat: 'VITE_DB_NAME_UAT',
  live: 'VITE_DB_NAME_LIVE',
};

interface Environment {
  environment: string;
  graphqlUrl: string;
  region: string;
  userPoolId: string;
  userPoolClientId: string;
  oauthDomain: string;
}

interface EnvironmentState {
  currentEnvironment: EnvironmentKey;
  compareEnvironment: EnvironmentKey | '';
  environments: Record<EnvironmentKey, Environment>;
}

// Helper function for redirect URLs
const getRedirectUrls = (): string[] => {
  const currentOrigin = window.location.origin + '/';
  const allUrls = [
    'https://staging.dls9znnb5zznz.amplifyapp.com/',
    'https://account-origination.dls9znnb5zznz.amplifyapp.com/',
    'https://los-hazel.velera.com/'
  ];
  
  return [currentOrigin, ...allUrls.filter(url => url !== currentOrigin)];
};

const logger = useLogger('environmentStore');

export const useEnvironmentStore = defineStore('environment', {
  state: (): EnvironmentState => ({
    currentEnvironment: (localStorage.getItem('selectedEnvironment') as EnvironmentKey) || 'dev',
    compareEnvironment: (localStorage.getItem('compareEnvironment') as EnvironmentKey | '') || '',
    environments: {
      dev: {
        environment: 'dev',
        graphqlUrl: import.meta.env.VITE_DEV_GRAPHQL_URL,
        region: import.meta.env.VITE_APPSYNC_REGION,
        userPoolId: import.meta.env.VITE_USER_POOL_ID,
        userPoolClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID,
        oauthDomain: import.meta.env.VITE_OAUTH_DOMAIN
      },
      test: {
        environment: 'test',
        graphqlUrl: import.meta.env.VITE_TEST_GRAPHQL_URL,
        region: import.meta.env.VITE_APPSYNC_REGION,
        userPoolId: import.meta.env.VITE_USER_POOL_ID,
        userPoolClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID,
        oauthDomain: import.meta.env.VITE_OAUTH_DOMAIN
      },
      uat: {
        environment: 'uat',
        graphqlUrl: import.meta.env.VITE_UAT_GRAPHQL_URL,
        region: import.meta.env.VITE_APPSYNC_REGION,
        userPoolId: import.meta.env.VITE_USER_POOL_ID,
        userPoolClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID,
        oauthDomain: import.meta.env.VITE_OAUTH_DOMAIN
      },
      live: {
        environment: 'live',
        graphqlUrl: import.meta.env.VITE_LIVE_GRAPHQL_URL,
        region: import.meta.env.VITE_APPSYNC_REGION,
        userPoolId: import.meta.env.VITE_USER_POOL_ID,
        userPoolClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID,
        oauthDomain: import.meta.env.VITE_OAUTH_DOMAIN
      }
    }
  }),
  
  getters: {
    current: (state): Environment => state.environments[state.currentEnvironment],
    compare: (state): Environment | null => 
      state.compareEnvironment ? state.environments[state.compareEnvironment] : null,
    availableEnvironments: (state): EnvironmentKey[] => 
      Object.keys(state.environments) as EnvironmentKey[],
    dbName(state): string {
      const envVarName = DB_NAME_ENV_MAP[state.currentEnvironment];
      return (import.meta.env[envVarName] as string) ?? '';
    },
    isComparing: (state): boolean => state.compareEnvironment !== ''
  },
  
  actions: {
    switchEnvironment(envKey: EnvironmentKey) {
      const previousEnv = this.currentEnvironment;
      this.currentEnvironment = envKey;
      localStorage.setItem('selectedEnvironment', envKey);
      logger.info('Environment switched', { from: previousEnv, to: envKey });
      
      // Clear all caches when environment changes to prevent stale data
      try {
        const cacheStore = useCacheStore();
        cacheStore.clearCache();
        logger.debug('Cache cleared on environment switch');
      } catch (e) {
        // Cache store may not be available during initialization
      }
      
      // Reconfigure Amplify with new environment
      const env = this.environments[envKey];
      const amplifyConfig: ResourcesConfig = {
        Auth: {
          Cognito: {
            userPoolId: env.userPoolId,
            userPoolClientId: env.userPoolClientId,
            loginWith: {
              oauth: {
                domain: env.oauthDomain,
                redirectSignIn: getRedirectUrls(),
                redirectSignOut: getRedirectUrls(),
                responseType: 'code' as const,
                scopes: ['openid', 'email', 'profile'],
                providers: ['ManagementAccountSAML' as any]
              }
            }
          }
        },
        API: {
          GraphQL: {
            endpoint: env.graphqlUrl,
            region: env.region,
            defaultAuthMode: 'userPool' as const
          }
        }
      };
      
      Amplify.configure(amplifyConfig);
      
      // Emit event for backward compatibility during migration
      window.dispatchEvent(new CustomEvent('environmentChanged', { 
        detail: { environment: envKey } 
      }));
    },
    
    setCompareEnvironment(envKey: EnvironmentKey) {
      this.compareEnvironment = envKey;
      localStorage.setItem('compareEnvironment', envKey);
      logger.info('Compare environment set', { compareEnvironment: envKey });
    },
    
    clearCompareEnvironment() {
      const previousCompare = this.compareEnvironment;
      this.compareEnvironment = '';
      localStorage.removeItem('compareEnvironment');
      logger.info('Compare environment cleared', { previousCompare });
    }
  }
});
